import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  QeukHeroAttribution,
  QeukTrustNudge,
  QeukPaymentPlaceholder,
  QeukFooter,
} from './QeukAttribution';
import { MarketingGallery } from './LandingTN';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --------------------------------------------------------------------------- //
// Bangladesh preview landing — Sprint 8.5 mirror of /tn.
// Auto-switches to Bengali (`bn`), captures Bangladeshi WhatsApp numbers.
// --------------------------------------------------------------------------- //
export default function LandingBD() {
  const { t, i18n } = useTranslation();
  const [wa, setWa] = useState('');
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (['en'].includes(i18n.language)) i18n.changeLanguage('bn');
  }, [i18n]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const digits = wa.replace(/[^\d]/g, '');
      const number = digits.startsWith('880') ? `+${digits}` : `+880${digits.replace(/^0/, '')}`;
      await axios.post(`${API}/waitlist/bd`, { whatsapp: number, source: 'landing_bd' });
      setStatus('done');
    } catch (e) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50" data-testid="bd-landing">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <nav className="flex justify-between items-center mb-8">
          <div>
            <p className="text-xs text-muted-foreground uppercase">Genius One Plus</p>
            <p className="text-lg font-bold">বাংলাদেশ</p>
          </div>
          <Link to="/" className="text-sm underline" data-testid="bd-back-link">← Back to Sri Lanka site</Link>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-200 text-emerald-900 text-xs font-semibold mb-4"
                  data-testid="bd-badge">
              {t('waitlist.eta.coming_2027') || 'Coming 2027'}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="bd-hero-title">
              {t('bd.hero.title') || 'Genius One Plus is coming to Bangladesh'}
            </h1>
            <QeukHeroAttribution testidPrefix="bd" />
            <p className="text-lg text-muted-foreground mb-6" data-testid="bd-hero-sub">
              {t('bd.hero.sub') || 'Dhaka • Chittagong • Sylhet — Genius One Plus is coming'}
            </p>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {t('bd.waitlist.title') || 'Notify me when we launch in Bangladesh'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {status === 'done' ? (
                  <div className="text-emerald-700 text-sm" data-testid="bd-waitlist-thanks">
                    ✓ {t('bd.waitlist.done') || 'You are on the list. We will WhatsApp you when we launch.'}
                  </div>
                ) : (
                  <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
                    <Input placeholder="+880 1712 345678" value={wa} onChange={(e) => setWa(e.target.value)}
                           data-testid="bd-waitlist-input" required />
                    <Button type="submit" disabled={status === 'loading'} data-testid="bd-waitlist-submit">
                      {status === 'loading' ? 'Sending…' : (t('bd.waitlist.cta') || 'Notify me')}
                    </Button>
                  </form>
                )}
                {status === 'error' && (
                  <p className="text-xs text-red-600 mt-2">Please enter a valid Bangladeshi WhatsApp number.</p>
                )}
                <p className="text-xs text-muted-foreground mt-3">
                  {t('bd.pricing.tba') || '৳ (taka) pricing to be announced closer to launch. No purchases yet.'}
                </p>
                <QeukTrustNudge testidPrefix="bd" />
              </CardContent>
            </Card>
            <QeukPaymentPlaceholder testidPrefix="bd" />
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-emerald-200/40 rounded-3xl -rotate-2" />
            <img src="/marketing/hero-08-ai-dial-watercolor.jpeg"
                 alt="Children exploring AI"
                 className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
                 data-testid="bd-hero-img" />
          </div>
        </div>

        <MarketingGallery />

        <div className="mt-10 text-center">
          <QeukFooter testidPrefix="bd" />
        </div>
      </div>
    </div>
  );
}
